// The Library's view model, from the rows load.js reads and the app's clock (lib/clock.js), so
// every time-based choice is made here and can be frozen for tests. Pure: no network, no React.
//
// The rules (design 5.3, the Main and PhoneLibrary boards, docs/data-model.md question 15):
// - Tiles: every paid product the person has access to that counts, secured, active, overdue,
//   suspended or paused, plus an open 1-2-1 space. Ended and revoked draw nothing. A live course
//   whose intake hasn't started yet sits under Starting soon; the rest under In progress, the
//   product used last first. A mastermind open to them says where they stand before its status, once
//   their log's level has come (d.levels, levels.js; the Accelerator critique's member 17, 22 Oct).
// - Next up: the 1 thing to do next. A session in the next 48 hours beats paying a secured place's
//   balance, which beats doing Start here before a live course's first session, which beats a
//   half-watched recording, which beats a masterclass not yet started (member review, 11 Oct, for
//   the balance and Start here). Nothing applies, no card. A session's Next up has Join from
//   join_opens_minutes before it starts until it ends, the same rule and link as Coming up's (Adam,
//   3 Oct). The balance's has the place secured page's own pay button (lib/balance.js).
// - Coming up: each product's next 2 sessions they can join, soonest first, at most 4, each with
//   Add to calendar, and Join from join_opens_minutes before it starts (design 5.3 and the
//   sign-off's Library line; the board draws neither, so it's on the departure list). The card for
//   the session Next up shows has no Add to calendar of its own, since Next up has it (Adam, 3
//   Oct). Webinars aren't in it: Zoom emails the people who register (decision 30).
// - Free webinars (Adam, 9 Oct, in place of the board's next 2 and the latest replay): Coming up,
//   every shown webinar that hasn't ended yet (today's included), soonest first, each opening its own
//   card on Free webinars (#webinar-<id>); then Watch again, every replay that's up, newest first, a
//   library of thumbnail cards, each opening its own page, where it plays (/free-webinars/replay/<id>;
//   Adam, 23 Oct). webinarCards.js.
// - Free resources: every live free item, in the catalogue's order.
// - Yours for life: a paid product whose access has ended keeps a tile, only while it has yours-for-life
//   items for them (hub.my_for_life), and the tile opens its Resources page, where only those items
//   show (Adam, 3 Oct, docs/stage4-for-adam.md 6). No board draws it.
// - Portal cards (Adam, 10 Oct): a product still on its own site that the person has an account for
//   there (hub.my_elsewhere: the Accelerator on members.adamslee.co.uk, POYT on course.adamslee.co.uk)
//   gets a card after the hub's own In progress tiles, in the catalogue's order, drawn as the Main
//   board draws that product's tile, and opening that site in a new tab, where they log in as they do
//   today. It's in no other part of the Library: not Next up, Coming up, search or the banner. It does
//   count towards "Everything you join shows up here", which only shows while there's at most 1 tile.
import * as f from '../lib/format.js'
import { greetingLine } from '../lib/names.js'
import { productTypeByName } from '../config/products.js'
import { accentKey, artKind, kindLine, isCashflow } from './looks.js'
import { portalTiles } from './portal.js'
import housePhoto from '../assets/pcfl-house.jpg'
import { buildSearchIndex } from './search.js'
import { searchThumbIndex } from '../product/recordingThumbs.js'
import { forLifeProducts, forLifeLine } from './forLife.js'
import { webinarCards } from './webinarCards.js'
import { paidByOther, payerPayment, payerPaymentShort, payerSorts, placeWord } from '../billing/payer.js'
import { balanceAmount, payHref, payLabel } from '../lib/balance.js'
import { LOG } from '../products/mastermind/logWords.js'

export { forLifeProducts, forLifeLine }
// the tile's look and kind line, in a module of their own so a test can import them (looks.js)
export { accentKey, artKind, kindLine }

const OPEN = ['active', 'overdue']
const DRAWN = ['secured', 'active', 'overdue', 'suspended', 'paused']
const RANK = { active: 5, overdue: 4, secured: 3, suspended: 2, paused: 1 }
const MIN = 60e3
const HOURS_48 = 48 * 60 * MIN
const NUMBERED_TITLE = /^(Week|Module|Session|Part)\b/

const LOOK_KEY = { master: 'master', accelerator: 'acc', funding_engine: 'fe', poyt: 'poyt', own: 'master' }

// A mastermind tile's word for where they stand (levels.js), the log's own words: never Getting started
const LEVEL_WORDS = Object.fromEntries(['on_track', 'a_little_behind', 'behind', 'well_behind'].map(k => [k, LOG.state[k]]))

const openLabel = p => (p.type === 'masterclass' ? 'Open the video' : `Open ${p.name}`)
const released = (r, nowMs) => r.is_published && r.published_at && Date.parse(r.published_at) <= nowMs

// The products this person has, from their own access rows only (staff at 2-step login can read
// every catalogue row, so the catalogue alone isn't enough).
export function ownedProducts(d, nowMs) {
  const byId = new Map(d.products.map(p => [p.id, p]))
  const owned = new Map()
  for (const a of d.access) {
    if (!DRAWN.includes(a.status)) continue
    if (a.ends_at && Date.parse(a.ends_at) <= nowMs) continue
    const p = byId.get(a.product_id)
    if (!p || p.state !== 'live' || p.access_kind !== 'paid') continue
    const o = owned.get(p.id) || { product: p, access: a, rows: [] }
    o.rows.push(a)
    if (RANK[a.status] > RANK[o.access.status]) o.access = a
    owned.set(p.id, o)
  }
  for (const s of d.spaces) {
    const p = byId.get(s.product_id)
    if (!p || p.state !== 'live' || owned.has(p.id)) continue
    owned.set(p.id, { product: p, access: { status: 'active', starts_at: s.opened_at }, rows: [], space: s })
  }
  for (const o of owned.values()) {
    const named = name => d.cohorts.find(c => c.product_id === o.product.id && c.name === name)
    o.cohort = o.access.cohort_name ? named(o.access.cohort_name) || null : null
    o.openCohortIds = o.rows.filter(r => OPEN.includes(r.status) && r.cohort_name).map(r => named(r.cohort_name)?.id).filter(Boolean)
    o.open = OPEN.includes(o.access.status)
  }
  return owned
}

// A Yours for life tile: the product's look and name, what stays, and the way to its Resources page
function forLifeTile({ product: p, items }) {
  return {
    id: p.id, href: `${p.path || ''}/resources`, name: p.name, art: artKind(p), accent: accentKey(p.look, p.type),
    cohortLabel: null, cover: p.type === 'on_demand' && isCashflow(p) ? housePhoto : null, kind: kindLine(p),
    status: forLifeLine(items), strong: false, pct: null, pctLabel: null, locked: null, soon: false, soonAt: null,
    lastOpened: null, forLife: true,
  }
}

// A session's Zoom link, only ever an https address (the column's own check says the same)
const zoomLink = url => (typeof url === 'string' && /^https:\/\//.test(url) ? url : null)

// What Add to calendar puts in the file: the hub's page for the product, never the Zoom link
const calendarFor = s => ({ uid: s.id, title: `${s.product.name}, ${s.title}`, startMs: s.start, endMs: s.end, path: s.product.path || '/' })

// The live sessions they can join, as can_see_session has it, worked out from their own rows: every
// one, those that have ended included (buildModel keeps those still to come for Next up and Coming up,
// soonest first; Start here's Next up asks whether a course's first session is still ahead)
function visibleSessions(d, owned) {
  const list = []
  for (const s of d.sessions) {
    const o = owned.get(s.product_id)
    if (!o || !o.open || s.status === 'cancelled' || !s.starts_at) continue
    const p = o.product
    const visible = p.live_sessions_audience === 'everyone'
      || (!s.cohort_id && p.type === 'mastermind')
      || (s.cohort_id && o.openCohortIds.includes(s.cohort_id))
    if (!visible) continue
    const start = Date.parse(s.starts_at)
    const mod = s.module_id ? d.modules.find(m => m.id === s.module_id) : null
    list.push({
      id: s.id, product: p, start, end: start + s.duration_minutes * MIN,
      joinAt: start - (p.join_opens_minutes ?? 15) * MIN,
      zoomUrl: zoomLink(s.zoom_join_url),
      title: s.title,
      cardTitle: mod && p.type !== 'mastermind' && !f.isPlaceholder(mod.title) && !s.title.includes(mod.title)
        ? `${s.title}: ${mod.title}` : s.title,
      numbered: NUMBERED_TITLE.test(s.title),
    })
  }
  for (const cs of d.clientSessions) {
    const space = d.spaces.find(s => s.id === cs.space_id)
    const o = space && owned.get(space.product_id)
    if (!o || !cs.starts_at) continue
    const start = Date.parse(cs.starts_at)
    const title = cs.title || `Session ${cs.number}`
    list.push({
      id: cs.id, product: o.product, start, end: start + cs.duration_minutes * MIN,
      joinAt: start - (o.product.join_opens_minutes ?? 15) * MIN,
      zoomUrl: zoomLink(cs.zoom_join_url),
      title, cardTitle: title, numbered: NUMBERED_TITLE.test(title), client: true,
    })
  }
  return list
}

function moment(s, nowMs) {
  const u = f.uk(s.start)
  return {
    live: s.start <= nowMs && nowMs < s.end,
    joinOpen: nowMs >= s.joinAt,
    days: f.daysFrom(nowMs, s.start),
    evening: u.hh >= 17,
    time: f.time(u),
  }
}

// Join's link: the session's Zoom link, from join_opens_minutes before it starts until it ends,
// for Next up and Coming up alike. visibleSessions has only the member's own sessions.
function joinLink(s, nowMs) {
  const m = moment(s, nowMs)
  return (m.live || m.joinOpen) && s.zoomUrl ? s.zoomUrl : null
}

// "Week 4 live tonight, 7pm", for a tile
function tileSessionLine(s, nowMs) {
  const m = moment(s, nowMs)
  if (m.live) return `${s.title} is live now`
  if (m.days === 0) return `${s.title} live ${m.evening ? 'tonight' : 'today'}, ${m.time}`
  if (m.days === 1) return `${s.title} live tomorrow, ${m.time}`
  return `${s.title} on ${f.shortDate(s.start)}, ${m.time}`
}

// "Tonight, 7pm. Join opens 6:45pm" on a laptop, "Tonight, 7pm" on a phone
function comingUpTime(s, nowMs, withJoin) {
  const m = moment(s, nowMs)
  if (m.live) return 'Live now'
  if (m.days === 0) {
    const when = `${m.evening ? 'Tonight' : 'Today'}, ${m.time}`
    if (!withJoin) return when
    return m.joinOpen ? `${when}. Join is open` : `${when}. Join opens ${f.time(s.joinAt)}`
  }
  if (m.days === 1) return `Tomorrow, ${m.time}`
  return m.time
}

function moduleLabelFor(d, moduleId) {
  const mod = moduleId && d.modules.find(m => m.id === moduleId)
  return mod ? mod.label : null
}

// The half-watched recording they played last, in 1 product or across everything they can open
function halfWatched(d, owned, nowMs, productId) {
  let best = null
  for (const pr of d.progress) {
    if (pr.watched_at || pr.position_seconds < 60) continue
    const r = d.recordings.find(x => x.id === pr.recording_id)
    if (!r || !released(r, nowMs)) continue
    if (productId ? r.product_id !== productId : !owned.get(r.product_id)?.open) continue
    if (!best || Date.parse(pr.last_played_at) > Date.parse(best.pr.last_played_at)) best = { pr, r }
  }
  return best
}

// A secured place's balance (member review, 11 Oct): "Pay your £597+vat balance by Tue 20 Oct and
// everything opens", with the place secured page's own pay button, for the place whose balance is due
// first. A secured place opens nothing (decision 35), so it's the 1 thing to do there. Without its
// payment link the button opens the product's page, which says Adam sends the link; without an amount
// from Stripe the words leave it out; once the due date has passed, nothing changes on its own, so
// the words only stop giving a date to pay by.
const dueMs = o => (o.access.balance_due_at ? Date.parse(o.access.balance_due_at) : Infinity)
function balanceUp(d, owned, person, nowMs) {
  const o = [...owned.values()].filter(x => x.access.status === 'secured').sort((a, b) => dueMs(a) - dueMs(b))[0]
  if (!o) return null
  const p = o.product
  const dueAt = o.access.balance_due_at || null
  const amount = balanceAmount(d.balances, p.id)
  const place = (d.places || []).find(x => x.product_id === p.id)
  const href = payHref(place?.pay_url, person?.email)
  const by = dueAt && Date.parse(dueAt) > nowMs ? ` by ${f.shortDate(dueAt)}` : ''
  const was = dueAt && !by ? ` The balance was due ${f.shortDate(dueAt)}.` : ''
  return {
    accent: accentKey(p.look, p.type),
    title: `Pay your ${amount ? `${amount} ` : ''}balance${by} and everything opens`,
    line: `Your place on ${p.name} is secured.${was}`,
    phoneLine: null,
    button: href ? { label: payLabel(amount), href } : { label: openLabel(p), href: p.path || '/' },
    calendar: null,
    join: null,
  }
}

// Start here before a live course's first session (member review, 11 Oct): while the first session
// of their intake is still ahead and Start here isn't done, "Do Start here before Week 1 on Wed 21 Oct",
// opening Start here. The course whose first session is soonest; inside the 48 hours before it, the
// session's own Next up comes first (nextUp).
function startHereUp(d, owned, visible, nowMs) {
  let best = null
  for (const o of owned.values()) {
    const p = o.product
    if (!o.open || p.type !== 'live_cohort') continue
    const mine = visible.filter(s => s.product.id === p.id).sort((a, b) => a.start - b.start)
    const first = mine[0]
    if (!first || first.start <= nowMs) continue
    const rows = d.steps.filter(st => st.product_id === p.id && st.section === 'start_here' && st.is_published)
    if (!rows.length) continue
    const done = rows.filter(st => d.stepProgress.some(sp => sp.step_id === st.id && sp.done_at)).length
    if (done === rows.length) continue
    if (!best || first.start < best.first.start) best = { o, first, done, total: rows.length }
  }
  if (!best) return null
  const { o, first, done, total } = best
  const p = o.product
  return {
    accent: accentKey(p.look, p.type),
    title: `Do Start here before ${first.numbered ? first.title : 'your first session'} on ${f.shortDate(first.start)}`,
    line: `Start here for ${p.name}: ${done} of ${total} done.`,
    phoneLine: null,
    button: { label: 'Open Start here', href: `${p.path || ''}/start-here` },
    calendar: null,
    join: null,
  }
}

function nextUp(d, owned, sessions, visible, person, nowMs) {
  const s = sessions.find(x => x.start - nowMs <= HOURS_48)
  if (s) {
    const m = moment(s, nowMs)
    const when = m.days === 0 ? `${m.evening ? 'tonight' : 'today'} at ${m.time}`
      : m.days === 1 ? `tomorrow at ${m.time}`
      : `on ${f.weekday(s.start)} at ${m.time}`
    // a 1-2-1 isn't "live": "Session 3 of your 1-2-1 is tomorrow at 7pm", as the ClientSpace
    // board's "Session 3 is on Thursday at 7pm" (Adam, 3 Oct)
    const title = s.client
      ? `${s.numbered ? `${s.title} of your 1-2-1` : `${s.product.name}: ${s.title}`} is ${m.live ? 'on now' : when}`
      : `${s.numbered ? `${s.title} of ${s.product.name}` : `${s.product.name}: ${s.title}`} is live ${m.live ? 'now' : when}`
    const join = m.live || m.joinOpen ? 'Join is open now.' : `Join opens at ${f.time(s.joinAt)}.`
    const hw = halfWatched(d, owned, nowMs, s.product.id)
    let catchUp = ''
    if (hw) {
      const mins = Math.floor(hw.pr.position_seconds / 60)
      const label = moduleLabelFor(d, hw.r.module_id)
      catchUp = ` You're ${f.minutes(mins)} into ${label ? `${label}'s recording` : hw.r.title} if you want to finish it first.`
    }
    const zoom = joinLink(s, nowMs)
    return {
      sessionId: s.id,
      accent: accentKey(s.product.look, s.product.type),
      title,
      line: join + catchUp,
      phoneLine: join,
      button: { label: openLabel(s.product), href: s.product.path || '/' },
      calendar: calendarFor(s),
      // Join, with the same name as Coming up's: "Join: The Funding Engine, Week 4"
      join: zoom ? { href: zoom, label: `Join: ${s.product.name}, ${s.cardTitle}` } : null,
    }
  }

  const pay = balanceUp(d, owned, person, nowMs)
  if (pay) return pay
  const startHere = startHereUp(d, owned, visible, nowMs)
  if (startHere) return startHere

  const hw = halfWatched(d, owned, nowMs, null)
  if (hw) {
    const p = owned.get(hw.r.product_id).product
    const mins = f.minutes(Math.floor(hw.pr.position_seconds / 60))
    const label = moduleLabelFor(d, hw.r.module_id)
    const isMc = p.type === 'masterclass'
    return {
      accent: accentKey(p.look, p.type),
      title: isMc ? `Carry on with ${p.name}` : `Carry on with ${label ? `${label} of ${p.name}` : hw.r.title}`,
      line: isMc ? `You're ${mins} in. It picks up where you left off, on any device.` : `You're ${mins} into the recording. It picks up where you left off, on any device.`,
      phoneLine: null,
      button: { label: openLabel(p), href: p.path || '/' },
      calendar: null,
      join: null,
    }
  }

  for (const o of owned.values()) {
    if (!o.open || o.product.type !== 'masterclass') continue
    const r = d.recordings.filter(x => x.product_id === o.product.id).sort((a, b) => a.sort_order - b.sort_order)[0]
    if (!r || d.progress.some(pr => pr.recording_id === r.id)) continue
    const c = d.content.find(x => x.recording_id === r.id)
    return {
      accent: accentKey(o.product.look, o.product.type),
      title: `Press play on ${o.product.name}`,
      line: `${c?.duration_seconds ? f.duration(c.duration_seconds) + '. ' : ''}It picks up where you leave off, on any device.`,
      phoneLine: null,
      button: { label: openLabel(o.product), href: o.product.path || '/' },
      calendar: null,
      join: null,
    }
  }
  return null
}

function monthsInto(startIso, nowMs) {
  const a = f.uk(startIso), b = f.uk(nowMs)
  return (b.y - a.y) * 12 + (b.m - a.m) - (b.d < a.d ? 1 : 0) + 1
}

// A mastermind's month of its term, as its home counts it: hub.my_mastermind's term_month (payments
// taken on a Stripe subscription, calendar months for a grant; 13 once past a 12-month term), so the
// tile, Account and the home agree. my_mastermind answers only while the product is open to them, so a
// paused or suspended membership (whose home is the paused page) counts calendar months from its
// start, as before, to the end of the term. { month, of, past } or null.
export function termMonth(d, product, access, nowMs) {
  if (product?.type !== 'mastermind' || !product.term_months) return null
  const mm = (d?.mastermind || []).find(m => m.product_id === product.id && m.term_month)
  if (mm) {
    const of = mm.term_months || product.term_months
    return { month: mm.term_month, of, past: mm.term_month > of }
  }
  if (!access?.starts_at) return null
  return { month: Math.min(Math.max(monthsInto(access.starts_at, nowMs), 1), product.term_months), of: product.term_months, past: false }
}
// "Month 3 of 12", or "Month 13, past the 12-month term" (the home's words)
export const termWords = t => (t.past ? `Month ${t.month}, past the ${t.of}-month term` : `Month ${t.month} of ${t.of}`)

// Whether their intake of a live course has finished: its last day (ends_on) has gone. An intake with
// no end date (admin's Add cohort gives none, and no screen sets 1) has finished once it has a session
// and every 1 of them is dated and over, as the database's own intake rule has it
// (20261010233000_fe_buyer_intake.sql); d.sessions holds their own intake's, undated ones included. With
// no intake (a delegate copied in without 1), whenever no session of theirs is still to come.
function intakeFinished(o, d, next, nowMs) {
  if (!o.cohort) return !next
  if (o.cohort.ends_on) return f.calendarDay(o.cohort.ends_on).day < f.uk(nowMs).day
  const mine = d.sessions.filter(s => s.cohort_id === o.cohort.id && s.status !== 'cancelled')
  return mine.length > 0 && mine.every(s => s.starts_at && Date.parse(s.starts_at) + s.duration_minutes * MIN <= nowMs)
}

function tileFor(o, d, sessions, nowMs) {
  const p = o.product, a = o.access
  const t = {
    id: p.id, href: p.path || '/', name: p.name, art: artKind(p), accent: accentKey(p.look, p.type),
    cohortLabel: o.cohort ? o.cohort.name : null, cover: null, kind: kindLine(p),
    status: '', strong: false, pct: null, pctLabel: null, locked: null, soon: false, soonAt: null,
    lastOpened: d.state.find(s => s.product_id === p.id)?.last_opened_at || null, level: null,
  }
  const next = sessions.find(s => s.product.id === p.id)
  const recs = d.recordings.filter(r => r.product_id === p.id).sort((x, y) => x.sort_order - y.sort_order)
  const mods = d.modules.filter(m => m.product_id === p.id && m.number != null).sort((x, y) => x.sort_order - y.sort_order)

  if (p.type === 'live_cohort') {
    const startsOn = o.cohort?.starts_on ? f.calendarDay(o.cohort.starts_on) : null
    if (startsOn && startsOn.day > f.uk(nowMs).day) {
      t.soon = true
      t.soonAt = startsOn.day
      const startHere = d.steps.filter(s => s.product_id === p.id && s.section === 'start_here' && s.is_published)
      t.status = `Starts ${f.shortDate(startsOn)}` + (startHere.length && o.open ? '. Start here is open' : '')
      if (startHere.length && o.open) {
        const done = startHere.filter(s => d.stepProgress.some(sp => sp.step_id === s.id && sp.done_at)).length
        t.pct = Math.round((done / startHere.length) * 100)
        t.pctLabel = `Start here: ${done} of ${startHere.length} done`
      }
    } else {
      t.status = next ? tileSessionLine(next, nowMs) : 'Catch up on the recordings'
      const total = o.cohort?.weeks_total || mods.length
      if (total) {
        // a week is out once its recording has opened to them: switched on, and for a course that
        // releases by intake (The Funding Engine, Profit On Your Terms), past their own session. Its
        // content comes back only then (recording_content), so a week switched on for everyone that
        // their intake hasn't reached yet doesn't count.
        const opened = new Set(d.content.map(c => c.recording_id))
        const out = m => recs.filter(r => r.module_id === m.id && released(r, nowMs) && opened.has(r.id))
        if (intakeFinished(o, d, next, nowMs)) {
          // once their intake has finished every week is out, so the bar counts the weeks they've
          // watched, as the player marks a recording watched (recording_progress.watched_at), and a week
          // is watched once every recording of it that's out is (member review, 11 Oct): "1 of 6 watched"
          const seen = id => d.progress.some(x => x.recording_id === id && x.watched_at)
          const watched = Math.min(mods.filter(m => { const rs = out(m); return rs.length > 0 && rs.every(r => seen(r.id)) }).length, total)
          t.pct = Math.round((watched / total) * 100)
          t.pctLabel = `${watched} of ${total} watched`
        } else {
          const n = Math.min(mods.filter(m => out(m).length > 0).length, total)
          t.pct = Math.round((n / total) * 100)
          t.pctLabel = `${n} of ${total} weeks`
        }
      }
    }
  } else if (p.type === 'mastermind') {
    // a session that has started is never the "next session": until it ends, the live line The Funding Engine's and
    // POYT's tiles use ("Group 2 session is live now"; the review of 22 Oct's small fixes)
    t.status = !next ? 'Next session date to come'
      : moment(next, nowMs).live ? tileSessionLine(next, nowMs)
        : `Next session ${f.shortDate(next.start)}, ${f.time(next.start)}`
    const term = termMonth(d, p, a, nowMs)
    if (term) {
      t.pct = Math.round((Math.min(term.month, term.of) / term.of) * 100)
      t.pctLabel = termWords(term)
    }
    // where they stand, before the status ("Behind. Next session Sun 25 Oct, 5pm", or during it "Behind. Group 2
    // session is live now"), once their log's level has come
    // (levels.js, read after the Library shows; the Accelerator critique's member 17): only while it's open to them,
    // since a payment's line takes the status's place otherwise
    const level = d.levels?.[p.id]
    if (a.status === 'active' && LEVEL_WORDS[level]) t.level = { key: level, word: LEVEL_WORDS[level] }
  } else if (p.type === 'masterclass') {
    const r = recs[0]
    const c = r && d.content.find(x => x.recording_id === r.id)
    const dur = c?.duration_seconds || null
    t.kind = kindLine(p, dur)
    const pr = r && d.progress.find(x => x.recording_id === r.id)
    if (pr?.watched_at) {
      // the full bar says it once; a label under it would say "Watched" again
      t.status = 'Watched'
      t.pct = 100
      t.pctLabel = null
    } else if (pr && (pr.position_seconds > 0 || pr.furthest_seconds > 0)) {
      t.status = `Carry on from ${f.clock(pr.position_seconds)}`
      if (dur) { t.pct = Math.min(99, Math.round((pr.furthest_seconds / dur) * 100)); t.pctLabel = `${t.pct}% watched` }
    } else {
      t.status = 'Press play to start'
      t.pct = 0
      t.pctLabel = 'Not started'
    }
  } else if (p.type === 'on_demand') {
    // the house is Property Cashflow For Life's own photo, never another course's
    if (isCashflow(p)) t.cover = housePhoto
    const watched = id => d.progress.some(x => x.recording_id === id && x.watched_at)
    const complete = m => {
      const mr = recs.filter(r => r.module_id === m.id && r.is_published)
      const hw = d.homework.filter(h => h.module_id === m.id && !h.removed_at)
      if (!mr.length && !hw.length) return false
      return mr.every(r => watched(r.id)) && hw.every(h => d.ticks.some(tk => tk.item_id === h.id))
    }
    const done = mods.filter(complete)
    const nextMod = mods.find(m => !complete(m))
    const started = d.progress.some(x => recs.some(r => r.id === x.recording_id))
    if (!mods.length) t.status = ''
    else if (!nextMod) t.status = 'Every module done'
    // a new course's module with no title yet is its label alone ("[Module 1 title]" is a placeholder)
    else t.status = `${started ? 'Carry on with' : 'Start with'} ${nextMod.label}${f.isPlaceholder(nextMod.title) ? '' : `, ${nextMod.title}`}`
    if (mods.length) {
      t.pct = Math.round((done.length / mods.length) * 100)
      t.pctLabel = `${done.length} of ${mods.length} modules`
    }
  } else if (p.type === 'client_space') {
    t.status = next ? `Next session ${f.shortDate(next.start)}, ${f.time(next.start)}` : 'Your private space with Adam'
  }

  // What the access itself says comes first (data-model.md 3.2)
  if (a.status === 'overdue') {
    t.status = a.suspend_on ? `Payment overdue. Open until ${f.dayMonth(a.suspend_on)}` : 'Payment overdue'
    t.strong = true
  } else if (a.status === 'suspended') {
    t.status = 'Paused until payment'
    t.strong = true
    t.locked = 'Paused until payment'
  } else if (a.status === 'paused') {
    t.status = a.paused_until ? `Paused until ${f.dayMonth(f.calendarDay(a.paused_until))}` : 'Paused'
    t.strong = true
    t.locked = t.status
  } else if (a.status === 'secured') {
    // "Place secured. Balance due Tue 20 Oct", from their own due date (member review, 11 Oct)
    t.status = a.balance_due_at ? `Place secured. Balance due ${f.shortDate(a.balance_due_at)}` : 'Place secured'
    t.strong = true
    t.pct = null
    t.pctLabel = null
  }
  return t
}

// 'Your Property Accelerator payment', as the board writes it: no 'The' after 'Your'
export const afterYour = name => name.replace(/^The\s+/, '')

// The product the banner is about: a suspension first, then an overdue payment
function paymentProblem(owned) {
  const rows = [...owned.values()]
  return rows.find(o => o.access.status === 'suspended') || rows.find(o => o.access.status === 'overdue') || null
}

// On a place someone else's subscription pays for (a partner seat, billing/payer.js), the payer's payment:
// "Marcus's payment for your Property Accelerator membership didn't go through on 25 September.
// Everything stays open while they sort it." (undrawn: the Accelerator's move, 11 Oct)
// A mastermind's says until when, from its access's suspend_on, as its tile and its own banner do: "Everything
// stays open until 2 October while you sort it" (the Accelerator critique's member 13, 20 Oct). Every other
// type's keeps the board's words, with no date.
function banner(owned) {
  const o = paymentProblem(owned)
  if (!o) return null
  const name = afterYour(o.product.name)
  const other = paidByOther(o.access)
  if (o.access.status === 'suspended') {
    return `Your ${name} access is paused until ${other ? payerPaymentShort(o.access) : 'your payment'} goes through. It opens again as soon as it does.`
  }
  const on = o.access.payment_failed_at ? ` on ${f.dayMonthLong(o.access.payment_failed_at)}` : ''
  const until = o.product.type === 'mastermind' && o.access.suspend_on ? ` until ${f.dayMonthLong(o.access.suspend_on)}` : ''
  if (other) return `${payerPayment(o.access, `${name} ${placeWord(o.product)}`)} didn't go through${on}. Everything stays open${until} ${payerSorts(o.access)}.`
  return `Your ${name} payment didn't go through${on}. Everything stays open${until} while you sort it.`
}

// What the banner's Update card opens: that product's billing, when there's a Stripe customer for it; none
// on a place someone else pays for (paidByOther)
function bannerFor(owned) {
  const o = paymentProblem(owned)
  return o ? { productId: o.product.id, hasCustomer: o.rows.some(r => r.has_customer), paidByOther: paidByOther(o.access) } : null
}

// The bell, newest first, except that a payment item for a product whose payment is still
// outstanding comes first, as the Main board puts it (its overdue and suspended Tweaks). Once
// the payment lands the item drops back into date order.
const PAYMENT_KINDS = { payment_failed: ['overdue', 'suspended'], access_paused: ['suspended'] }

function bell(d, owned) {
  const byDate = (a, b) => Date.parse(b.created_at) - Date.parse(a.created_at)
  const pinned = n => (PAYMENT_KINDS[n.kind] || []).includes(owned.get(n.product_id)?.access.status)
  const all = [...d.notifications].sort(byDate)
  return [...all.filter(pinned), ...all.filter(n => !pinned(n))]
}

function announcement(d, owned, nowMs) {
  const hidden = new Set(d.hides.map(h => h.announcement_id))
  const showing = d.announcements
    .filter(a => !a.taken_down_at && Date.parse(a.starts_at) <= nowMs
      && (!a.comes_down_at || Date.parse(a.comes_down_at) > nowMs)
      && (a.audience === 'everyone' || owned.has(a.product_id))
      && !hidden.has(a.id))
    .sort((x, y) => Date.parse(y.starts_at) - Date.parse(x.starts_at))[0]
  if (!showing) return null
  return {
    id: showing.id,
    from: `From Adam, ${f.shortDate(showing.starts_at)}`,
    headline: showing.headline,
    line: showing.body_line,
    button: showing.button_label ? { label: showing.button_label, href: showing.button_path } : null,
  }
}

// Free webinars' 2 parts (webinarCards.js), each dot the colour of what the webinar sells, from the
// my_replay_cta answers, gold when it sells nothing
function webinars(d, nowMs) {
  return webinarCards(d.webinars, nowMs, {
    accentFor: w => {
      const cta = d.ctas?.[w.id]
      return cta?.sells_look ? accentKey(cta.sells_look, productTypeByName(cta.sells_name)) : 'master'
    },
  })
}

function freebies(d) {
  return d.products
    .filter(p => p.access_kind === 'free' && p.state === 'live' && (p.type === 'linked' || p.type === 'resource_pack'))
    .sort((a, b) => a.sort_order - b.sort_order)
    .map(p => {
      const card = p.settings?.card || {}
      return {
        id: p.id, name: p.name, glyph: card.glyph || '',
        // its own cover photo, when 1 is saved: cropped to fill the square in place of the glyph (3 Nov)
        coverPath: p.cover_path || null,
        tone: `${LOOK_KEY[p.look] || 'master'}-${card.tone === 'solid' ? 'solid' : 'light'}`,
        kind: card.library_kind_line || card.kind_label || '',
        action: card.button_label || 'Open',
      }
    })
}

export function buildModel(d, person, nowMs) {
  const owned = ownedProducts(d, nowMs)
  const visible = visibleSessions(d, owned)
  const sessions = visible.filter(s => s.end > nowMs).sort((a, b) => a.start - b.start)
  const tiles = [...owned.values()].map(o => tileFor(o, d, sessions, nowMs))
  const byOrder = (a, b) => {
    const la = a.lastOpened ? Date.parse(a.lastOpened) : 0, lb = b.lastOpened ? Date.parse(b.lastOpened) : 0
    if (la !== lb) return lb - la
    return (owned.get(a.id).product.sort_order ?? 0) - (owned.get(b.id).product.sort_order ?? 0)
  }

  const next = nextUp(d, owned, sessions, visible, person, nowMs)
  // each product's next 2, soonest first, at most 4
  const perProduct = new Map()
  const comingUp = []
  for (const s of sessions) {
    const n = perProduct.get(s.product.id) || 0
    if (n >= 2) continue
    perProduct.set(s.product.id, n + 1)
    comingUp.push({
      id: s.id, block: f.dateBlock(s.start), product: s.product.name, accent: accentKey(s.product.look, s.product.type),
      title: s.cardTitle, time: comingUpTime(s, nowMs, true), phoneTime: comingUpTime(s, nowMs, false),
      // Join shows from join_opens_minutes before the start until the end, when there's a link
      join: joinLink(s, nowMs),
      // Next up's session has its Add to calendar there, not again here
      calendar: next?.sessionId === s.id ? null : calendarFor(s),
    })
    if (comingUp.length === 4) break
  }

  const notifications = bell(d, owned).map(n => ({
    id: n.id, text: n.body, href: n.path, unread: !n.read_at,
    when: f.daysFrom(nowMs, n.created_at) === 0 ? 'Today' : f.shortDate(n.created_at),
  }))
  // the hub's own tiles, then the portal cards (kept out of byOrder, which reads owned)
  const inProgress = [...tiles.filter(t => !t.soon).sort(byOrder), ...portalTiles(d.elsewhere, owned)]
  const forLife = [...forLifeProducts(d, owned).values()].map(forLifeTile)
  const startingSoon = tiles.filter(t => t.soon).sort((a, b) => a.soonAt - b.soonAt)

  return {
    dateLine: f.longDate(nowMs),
    // "Morning, Sam", or "Morning" alone when the first name is the database's "there" or none (lib/names.js)
    greeting: greetingLine(f.greetingWord(nowMs), person.first_name),
    initials: f.initials(person.full_name),
    banner: banner(owned),
    bannerFor: bannerFor(owned),
    announcement: announcement(d, owned, nowMs),
    nextUp: next,
    comingUp,
    inProgress,
    startingSoon,
    forLife,
    joinLine: inProgress.length + startingSoon.length <= 1,
    webinars: webinars(d, nowMs),
    freebies: freebies(d),
    notifications,
    hasUnread: notifications.some(n => n.unread),
    search: buildSearchIndex(d, owned, nowMs),
    // the search's recording hits' frames (decision 47, 12 Oct): only for a product with a thumbnail
    searchThumbs: searchThumbIndex(d, [...owned.values()].filter(o => o.open).map(o => o.product)),
  }
}
